import TournamentsService from '@/lib/Tournaments';
import { useTournamentStore } from '@/lib/Tournaments/store';
import { Tournament } from '@/lib/Tournaments/types';
import ArrayUtils from '@/utils/ArrayUtils';
import TournamentsUtils from '@/utils/TournamentsUtils';
import { InvestingTournamentStatus } from '@/lib/Tournaments/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import useEducationStats from './useEducationStats';
import useCurrentUser from './useCurrentUser';

export function useTournaments() {
  
  const {
    data: tournaments,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['tournaments'],
    queryFn: () => TournamentsService.getTournaments(),
    select: function (data) {
      const liveTournaments = data.filter((t) => t.status === 'InProgress');
      const incomingTournaments = data.filter((t) => t.status === 'Open');
      const pastTournaments = data.filter((t) => t.status === 'Finished' || t.status === 'Cancelled');

      if (ArrayUtils.isEmpty(incomingTournaments)) {
        this.refetchInterval = false;
      } 
   
      return {
        liveTournaments,
        incomingTournaments,
        pastTournaments,
      };
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: 30 * 1000,
    refetchIntervalInBackground: true,
  });

  const incomingTournaments = useMemo(() => {
    return tournaments?.incomingTournaments ?? [];
  }, [tournaments?.incomingTournaments]);

  const hasIncomingTournaments = useMemo(() => {
    return incomingTournaments.length > 0;
  }, [incomingTournaments]);

  return {
    tournaments,
    incomingTournaments,
    hasIncomingTournaments,
    isLoading,
    isError,
    refetch,
  };
}

export function useTournament(id?: string) {
  const queryClient = useQueryClient();
  const { setTournament, currentTournamentId } = useTournamentStore();
  const { stats: userStats, isLoading: isLoadingUserStats } = useEducationStats();
  const { data: user } = useCurrentUser();
  
  const {
    data: tournament,
    isLoading,
    isError,
    refetch,
  } = useQuery<Tournament>({
    queryKey: ['tournament', id],
    queryFn: async () => {
      const tournament = await TournamentsService.getTournament(id ?? currentTournamentId);
      let tournamentPortfolio = {};

      if (tournament?.isParticipating) {
        try {
          const { portfolio, ...rest } =
          (await TournamentsService.getTournamentPortfolio(tournament.id, user?.account)) ?? {};
        tournamentPortfolio = {
          ...rest,
          stocks: portfolio,
        };
        } catch (error) {
          console.error('Error fetching tournament portfolio:', error);
        }
      }

      return {
        ...tournament,
        name: TournamentsUtils.getName(tournament.name),
        institutionId: TournamentsUtils.getInstitutionId(tournament.name),
        portfolio: tournamentPortfolio,
      };
    },
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
    enabled: !!id
  });

  useEffect(() => {
    return () => {
      queryClient.removeQueries({
        queryKey: ['tournament', id],
      });
    };
  }, [id, queryClient]);

  useEffect(() => {
    if (tournament) setTournament(tournament);
  }, [tournament, setTournament]);

  const inTournament = useMemo(() => {
    if (tournament) {
      return tournament?.isParticipating;
    }
    return false;
  }, [tournament]);

  const isIncoming = useMemo(() =>{
    if (tournament) {
      return tournament?.status == InvestingTournamentStatus.Open;
    }
    return false;
  }, [tournament])

  const isFinished = useMemo(() =>{
    if (tournament) {
      return tournament?.status === InvestingTournamentStatus.Finished;
    }
    return false;
  }, [tournament])

  const isLive = useMemo(() =>{
    if (tournament) {
      return tournament?.status === InvestingTournamentStatus.InProgress;
    }
    return false;
  }, [tournament])

  const isCancelled = useMemo(() =>{
    if (tournament) {
      return tournament?.status === InvestingTournamentStatus.Cancelled;
    }
    return false;
  }, [tournament])

  const hasBuget = () => {
    return (
      !tournament?.portfolio?.stocks ||
      Number(tournament?.budget) >
        tournament?.portfolio?.stocks?.reduce((c, t) => (c += t.allocatedNotional), 0)
    );
  };

  if (!tournament) {
    return {
      tournament: null,
      type: null,
      isLoading,
      isError,
      refetch,
      isFinished,
      isIncoming,
      inTournament,
      isLive,
      isCancelled,
      hasBuget,
    };
  }

  return {
    tournament,
    type: tournament?.name && TournamentsUtils.getTournamentType(tournament?.name),
    isLoading: isLoading || isLoadingUserStats,
    isError,
    refetch,
    isFinished,
    isIncoming,
    inTournament,
    isCancelled,
    isLive,
    hasBuget,
    coins: userStats?.coins,
  };
}
